// Кнопка с надписью на плашке: ею игрок проходит то, что игра показывает поверх поля.
//
// Плашек в ассетах две. Медная 330x192 стоит в сообщении о потерянной связи, широкая 396x154 —
// на нижней створке заглушки поля, см. GDD, раздел 7.2. Кадров у каждой три: обычный,
// под курсором и нажатый.
//
// Кадр прямоугольный, поэтому область нажатия — сам кадр, и задавать её не надо. Кнопка живёт
// только вместе с тем, что её показало, поэтому вид она меняет прямо на событиях мыши,
// без таблицы переходов, см. ui/button.ts.
//
// Кнопка умеет нажиматься сама: на створке она ждёт игрока пару секунд и жмётся, если он
// не тронул её, см. ui/fieldCover.ts. Своё нажатие она показывает тем же кадром, что и мышь,
// иначе игрок не поймёт, что показ пошёл дальше сам.
//
// Пробел жмёт кнопку наравне с мышью, пока она на экране. Клавиша перехватывается на погружении
// и дальше не идёт: иначе тот же пробел дошёл бы до кнопки спина и оборвал бы показ.

import { Sprite, Text, type TextStyleOptions } from 'pixi.js';

import { sfx } from '../sound/sound';

import type { ButtonFaces, ButtonState } from './button';
import { fitLabel } from './label';

/** Сколько держится кадр нажатия, прежде чем кнопка сработает. */
const PRESS_SECONDS: number = 0.12;

/**
 * Какую долю кадра занимает лицо плашки, на котором стоит надпись.
 *
 * Остальное — медная рамка с завитками. У широкой плашки лицо идёт от 40 до 357 из 396,
 * у медной — от 38 до 293 из 330, то есть чуть больше трёх четвертей у обеих.
 */
const FACE_SHARE: number = 0.77;

/** Кнопка на плашке. */
export class PlateButton {
  readonly view: Sprite;

  readonly #faces: ButtonFaces;
  readonly #caption: Text;
  readonly #onPress: () => void;

  /** Сколько осталось держать кадр нажатия. Ноль — кнопка не нажата. */
  #held = 0;

  /**
   * @param faces Кадры плашки: обычный, под курсором и нажатый.
   * @param style Стиль надписи на плашке.
   * @param onPress Кого позвать, когда кнопка сработает.
   */
  constructor(faces: ButtonFaces, style: TextStyleOptions, onPress: () => void) {
    this.#faces = faces;
    this.#onPress = onPress;

    this.view = new Sprite(faces.idle);
    this.view.anchor.set(0.5);
    this.view.eventMode = 'static';
    this.view.cursor = 'pointer';

    this.view.on('pointerover', () => this.#show('hover'));
    this.view.on('pointerout', () => this.#show('idle'));
    this.view.on('pointerdown', () => this.#show('active'));
    this.view.on('pointerup', () => this.#show('hover'));
    this.view.on('pointerupoutside', () => this.#show('idle'));
    this.view.on('pointertap', () => this.press());

    this.#caption = new Text({ text: '', style });
    this.#caption.anchor.set(0.5);
    this.view.addChild(this.#caption);
  }

  /**
   * Надпись на плашке. Она идёт как есть, поэтому регистр задаёт зовущий.
   *
   * Длинный перевод ужимается под лицо плашки: «OK» по-испански набирается словом Aceptar,
   * и на завитки рамки оно лезть не должно, см. ui/label.ts.
   */
  set label(text: string) {
    this.#caption.text = text;
    fitLabel(this.#caption, { width: this.view.texture.width * FACE_SHARE });
  }

  /** Показывает кнопку и ловит пробел, пока она на экране. */
  show(): void {
    if (this.view.visible)
      return;

    this.#held = 0;
    this.view.visible = true;
    this.view.texture = this.#faces.idle;

    window.addEventListener('keydown', this.#onKeyDown, true);
  }

  /** Убирает кнопку и отпускает пробел. */
  hide(): void {
    if (!this.view.visible)
      return;

    this.#held = 0;
    this.view.visible = false;

    window.removeEventListener('keydown', this.#onKeyDown, true);
  }

  /**
   * Жмёт кнопку, как это сделала бы мышь.
   *
   * Кадр нажатия загорается сразу, а срабатывает кнопка через миг: так нажатие видно
   * и когда его сделала не мышь, а сама игра. Второе нажатие подряд ничего не меняет.
   *
   * Звук идёт вместе с кадром: он говорит о том же — кнопка нажата, и показ идёт дальше.
   */
  press(): void {
    if (!this.view.visible || this.#held > 0)
      return;

    this.#held = PRESS_SECONDS;
    this.view.texture = this.#faces.active;
    sfx('button');
  }

  /** Доводит нажатие: кадр отгорел, кнопка срабатывает. */
  update(seconds: number): void {
    if (this.#held <= 0)
      return;

    this.#held -= seconds;
    if (this.#held > 0)
      return;

    this.#held = 0;
    this.hide();
    this.#onPress();
  }

  /** Меняет вид кнопки. Нажатую кнопку мышь уже не касается: она досматривает свой кадр. */
  #show(state: ButtonState): void {
    if (this.#held > 0)
      return;

    this.view.texture = this.#faces[state];
  }

  /**
   * Жмёт кнопку с клавиатуры.
   *
   * Клавиша перехватывается на погружении и дальше не идёт: пока кнопка на экране, пробел
   * принадлежит ей, а не кнопке спина. Долгое удержание браузер повторяет, но кнопка
   * нажимается один раз.
   */
  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space')
      return;

    event.preventDefault();
    event.stopPropagation();

    if (!event.repeat)
      this.press();
  };
}
